'use client';

import { useMemo, useState } from 'react';
import CurrencySelector from '@/components/utils/CurrencySelector';
import CalculatorInputs from './CalculatorInputs';
import ResultsDisplay from './ResultsDisplay';
import { calculateFinancialIndependence } from './calculations';
import { FinancialIndependenceValues } from './types';
import { FinancialIndependenceTranslations } from './types';
import FinancialIndependenceChart from './DividendsChart';

interface Props {
  translations: FinancialIndependenceTranslations;
}

export default function DividendsCalculator({ translations }: Props) {
  const [values, setValues] = useState<FinancialIndependenceValues>({
    monthlyExpenses: 30000,
    dividendYield: 8,
    currentSavings: 0,
    monthlyContribution: 0,
    investmentReturn: 8,
    currency: 'kr',
  });

  const handleValuesChange = (field: keyof FinancialIndependenceValues, value: number | string) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  const result = useMemo(() => calculateFinancialIndependence(values), [values]);

  return (
    <section id="dividends-calculator" className="w-full my-12 tools">
      <div className="rounded-xl border border-gray-200 shadow-sm bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-blue-900">{translations.title}</h2>
              <p className="text-sm text-gray-600 mt-1">{translations.subtitle}</p>
            </div>
            <div className="w-56">
              <CurrencySelector
                value={values.currency}
                onChange={(currency) => handleValuesChange('currency', currency)}
                showArrow={false}
                localStorageKey="currencyPreference"
              />
            </div>
          </div>
        </div>
        <div className="p-6 space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              <CalculatorInputs values={values} onValuesChange={handleValuesChange} translations={translations} />
            </div>
            <div className="lg:col-span-2">
              <ResultsDisplay
                result={result}
                translations={translations}
                currency={values.currency}
              />
            </div>
          </div>
          <FinancialIndependenceChart
            values={values}
            result={result}
            translations={translations}
          />
          {/* Disclaimer */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h4 className="text-sm font-semibold text-yellow-900 mb-2">{translations.disclaimerTitle}</h4>
            <p className="text-sm text-yellow-800">
              {translations.disclaimerText}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
